import { Typography, Form, FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	Feedback,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useWizard } from '../../../components/wizard/useWizard';
import { usePreceptorInterview } from './usePreceptorInterviewWizard';
import { useCallback, useEffect } from 'react';
import { RecommendationComponent } from '../RecommendationComponent';
import {
	PredefinedReasonCategory,
	ReviewAction,
} from '../../../shared/Constants';
import { useLocation } from 'react-router-dom';
import { VIEW_PRECEPTOR_INTERVIEW } from '../../../shared/Routes';
import useUserSession from '../../../hooks/useUserSession';
import { ALLOWED_ROLES } from '../../../shared/Utils';

const { Text } = Typography;

interface PreceptorRecommendationStepProps {
	form: FormInstance;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="flex-grow text-primary-blue text-lg font-bold py-5">
				{heading}
			</Text>
			<Text className="mb-[8px]">{description}</Text>
		</div>
	);
};

export const PreceptorRecommendationStep = ({
	form,
}: PreceptorRecommendationStepProps) => {
	const { t } = useTranslation();
	const { selectedRole } = useUserSession();
	const { pathname } = useLocation();

	const isProposerAndViewable =
		selectedRole === UserRole.PROPOSER &&
		pathname.includes(VIEW_PRECEPTOR_INTERVIEW);

	const isRoleNotAllowed = !ALLOWED_ROLES.includes(selectedRole as UserRole);

	const disableEdit = isRoleNotAllowed || isProposerAndViewable;

	return (
		<div className="flex flex-col h-full min-h-0 max-w-[600px]">
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={form}
			>
				<SectionHeader
					heading={t('collectPreceptorRecommendation')}
					description={t('askPreceptorForHisOverallRecommendation')}
				/>
				<RecommendationComponent
					commentsMandatory
					commentsLabel="commentsFromPreceptorToSupportAboveRecommendation"
					commentsPlaceholder="commentsAreMandatory"
					form={form}
					category={PredefinedReasonCategory.PRECEPTOR_REJECT}
					displayComments
					disabled={disableEdit}
				/>
				<div className="py-[24px]">
					<Text className="font-medium">
						{t('preceptorWillReceiveAnEmail')}
					</Text>
				</div>
			</Form>
		</div>
	);
};

export const PreceptorRecommendationStepContainer = () => {
	const [form] = Form.useForm<Feedback>();
	const { setSubmitButtonDisabled } = useWizard();
	const values = Form.useWatch<Feedback>([], form);
	const { preceptorRecommendation, setPreceptorRecommendation } =
		usePreceptorInterview();

	const handleFormValuesChange = useCallback(async () => {
		try {
			const fieldValues = await form.validateFields({ validateOnly: true });
			setPreceptorRecommendation(fieldValues);
			if (
				fieldValues.review === ReviewAction.REJECT &&
				!fieldValues.predefinedReasons?.length
			) {
				setSubmitButtonDisabled(true);
			} else {
				setSubmitButtonDisabled(false);
			}
		} catch (e) {
			const fields = e as { values: Feedback };
			setSubmitButtonDisabled(true);
			setPreceptorRecommendation(fields.values);
		}
	}, [form, setPreceptorRecommendation, setSubmitButtonDisabled]);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	useEffect(() => {
		if (preceptorRecommendation) {
			form.setFieldsValue(preceptorRecommendation);
			setTimeout(() => {
				void handleFormValuesChange();
			}, 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form, handleFormValuesChange]);

	return <PreceptorRecommendationStep form={form} />;
};
